import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useLandingPageContext } from '@/features/campaigns/context/LandingPageContext';
import { useLiveCities } from '@/features/destinations/hooks/useLiveCities';
import { resolveCityImage } from '@/features/destinations/utils/cityImage';
import type { LiveCity } from '@/types';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] as const },
  },
};

const CARD_GRID_SIZE = { xs: 6, sm: 4, md: 3 } as const;
const CARD_ASPECT_RATIO = '4/5';

export function PopularDestinationsSection() {
  const { cities, label, isLoading } = useLiveCities();

  if (!isLoading && cities.length === 0) return null;

  return (
    <Box
      component="section"
      id="explore-experiences"
      sx={{
        pt: { xs: 7, sm: 8, md: 11 },
        pb: { xs: 4, sm: 5, md: 6 },
        bgcolor: '#FFFFFF',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 4, sm: 5, md: 6 },
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#16A34A',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              mb: 1.5,
            }}
          >
            Now Live
          </Typography>

          <Typography
            component="h2"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: '#0F172A',
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              mb: 1.5,
            }}
          >
            Explore Amazing Experiences
          </Typography>

          <Box
            sx={{
              width: 48,
              height: 3,
              borderRadius: 2,
              bgcolor: '#2563EB',
              mx: 'auto',
              mb: 1.5,
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: '0.9375rem', md: '1.0625rem' },
              color: '#64748B',
              maxWidth: 560,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            {label
              ? `Handpicked local guides across ${label} — book your next trip today.`
              : 'Handpicked local guides — book your next trip today.'}
          </Typography>
        </Box>

        {/* Centred so a partly filled last row (admins add any number of cities) stays balanced. */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} justifyContent="center">
          {isLoading &&
            Array.from({ length: 6 }, (_, index) => (
              <Grid key={index} size={CARD_GRID_SIZE}>
                <Skeleton
                  variant="rounded"
                  sx={{ width: '100%', height: 'auto', aspectRatio: CARD_ASPECT_RATIO, borderRadius: 3 }}
                />
              </Grid>
            ))}
          {cities.map((destination, index) => (
            <Grid key={destination._id} size={CARD_GRID_SIZE}>
              <Box
                component={motion.div}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: index * 0.08 }}
                sx={{ height: '100%' }}
              >
                <DestinationCard destination={destination} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

interface DestinationCardProps {
  destination: LiveCity;
}

function DestinationCard({ destination }: DestinationCardProps) {
  const { setSelectedCity } = useLandingPageContext();

  return (
    <Box
      component={RouterLink}
      to={`/guides?city=${encodeURIComponent(destination.name)}`}
      onMouseEnter={() => setSelectedCity(destination.name)}
      sx={{
        display: 'block',
        position: 'relative',
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        aspectRatio: CARD_ASPECT_RATIO,
        textDecoration: 'none',
        boxShadow: '0 2px 12px rgba(15, 23, 42, 0.08)',
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 16px 32px -8px rgba(15, 23, 42, 0.18)',
          '& .destination-image': {
            transform: 'scale(1.06)',
          },
          '& .destination-arrow': {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
      }}
    >
      <Box
        className="destination-image"
        component="img"
        src={resolveCityImage(destination.imageUrl)}
        alt={destination.region ? `${destination.name}, ${destination.region}` : destination.name}
        loading="lazy"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, rgba(15, 23, 42, 0.9) 0%, rgba(15, 23, 42, 0.4) 40%, rgba(15, 23, 42, 0) 70%)',
        }}
      />

      <Chip
        label="Live"
        size="small"
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          height: 22,
          fontSize: '0.625rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          bgcolor: 'rgba(22, 163, 74, 0.95)',
          color: '#FFFFFF',
          '& .MuiChip-label': { px: 1 },
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          p: { xs: 1.25, md: 1.75 },
          color: 'white',
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '1rem', md: '1.125rem' },
            fontWeight: 700,
            lineHeight: 1.2,
            mb: 0.25,
          }}
        >
          {destination.name}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '0.6875rem', md: '0.75rem' },
            color: 'rgba(255, 255, 255, 0.75)',
            mb: 0.5,
          }}
        >
          {destination.region}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '0.6875rem', md: '0.75rem' },
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: 1.4,
              flex: 1,
              minWidth: 0,
              // Admin-written highlights vary in length; keep every card the same height.
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {destination.highlight}
          </Typography>

          <ArrowForwardIcon
            className="destination-arrow"
            sx={{
              fontSize: 16,
              color: '#38BDF8',
              flexShrink: 0,
              opacity: 0.7,
              transform: 'translateX(-4px)',
              transition: 'all 200ms ease',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
